import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

import {
  sanitizeTelemetry,
  sanitizeTelemetryError,
  sanitizeTelemetryText,
  sanitizeTelemetryUrl,
} from '@borradh-workspace/observability/telemetry-sanitizer';
import { classifyDroppableEvent } from './sentry-filter';

import { getBrowserErrorEndpoint } from './browser-error-endpoint';

interface LogErrorContext {
  feature?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

interface QueuedCapture {
  error: unknown;
  feature: string;
  operation: string;
  context?: LogErrorContext;
  level: 'error' | 'warning';
}

/**
 * Errors raised before posthog-js finished initializing.
 *
 * Sentry initializes SYNCHRONOUSLY during `TelemetryBootstrap`'s first render;
 * posthog-js initializes in a `useEffect` in `PostHogProvider`, i.e. after
 * paint. Anything that fails in between used to hit `!posthog.__loaded` and be
 * dropped on the floor — Sentry-only by construction. That is the WEB-15
 * ("Intercom.sendPushTokenToIntercom is not implemented on ios", 12 events) and
 * WEB-33 (push-token timeout at sign-in) class: boot-time errors that existed in
 * Sentry and nowhere else.
 *
 * Bounded, because an error loop must not become a memory leak. The FIRST
 * entries are kept rather than the last: boot-time errors are the ones this
 * queue exists for, and a later flood is usually the same error repeating.
 */
const preInitQueue: QueuedCapture[] = [];
const PRE_INIT_QUEUE_LIMIT = 50;

/** Best-effort, redacted mirror of browser failures to Better Stack. */
function forwardToBetterStack(
  operation: string,
  error: unknown,
  level: 'error' | 'warn' = 'error'
): void {
  const err = sanitizeTelemetryError(error);
  const trim = (value: string, max: number) => value.slice(0, max);

  const apiFields = isApiFetchErrorLike(error)
    ? {
        api_status: error.status,
        api_method: error.method,
        api_path: sanitizeTelemetryUrl(error.path).slice(0, 500),
        ...(error.requestId && { api_request_id: error.requestId }),
      }
    : {};

  void fetch(getBrowserErrorEndpoint(), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(
      sanitizeTelemetry({
        msg: trim(err.message || operation, 4_000),
        message: trim(err.message || operation, 4_000),
        level,
        operation: trim(operation, 200),
        error_name: trim(err.name || 'Error', 200),
        error_code: (err as Error & { code?: unknown }).code,
        error_stack: trim(err.stack ?? '', 8_000),
        err: {
          type: trim(err.name || 'Error', 200),
          message: trim(err.message || operation, 4_000),
          stack: trim(err.stack ?? '', 8_000),
        },
        ...apiFields,
        source: 'posthog-browser-error-forwarder',
        service: 'dashboard-app',
        runtime: 'browser',
        dt: new Date().toISOString(),
      })
    ),
    keepalive: true,
  }).catch(() => undefined);
}

/**
 * Copy and scrub errors before queueing or passing them to PostHog.
 * Keep real stack locations and codes, then restore the user's DNT state.
 */
function captureToPostHog(
  error: unknown,
  feature: string,
  operation: string,
  context?: LogErrorContext,
  level: 'error' | 'warning' = 'error'
): void {
  if (isNoiseError(error)) return;
  const safeError = sanitizeTelemetryError(error);
  const safeContext = sanitizeTelemetry(context);
  const safeLabels = sanitizeTelemetry({ feature, operation });
  if (!posthog.__loaded) {
    if (preInitQueue.length < PRE_INIT_QUEUE_LIMIT) {
      preInitQueue.push({
        error: safeError,
        feature: safeLabels.feature,
        operation: safeLabels.operation,
        context: safeContext,
        level,
      });
    }
    return;
  }

  const wasOptedOut = posthog.has_opted_out_capturing();
  // captureEventName: false skips the automatic "opted-in" analytics event.
  if (wasOptedOut) posthog.opt_in_capturing({ captureEventName: false });

  posthog.captureException(
    safeError,
    sanitizeTelemetry({
      // posthog-js spreads these over the derived $exception_* properties, so
      // this overrides the default level (see logWarning).
      $exception_level: level,
      feature,
      operation,
      ...safeContext?.tags,
      ...safeContext?.extra,
      ...((safeError as Error & { code?: unknown }).code !== undefined && {
        code: (safeError as Error & { code?: unknown }).code,
      }),
    })
  );

  if (wasOptedOut) posthog.opt_out_capturing();
}

/**
 * Replay errors captured before posthog-js was ready. Call once, immediately
 * after `posthog.init`.
 *
 * Safe to call when PostHog never initializes (no key): the queue simply stays
 * put, capped at {@link PRE_INIT_QUEUE_LIMIT}.
 */
export function flushPostHogErrorQueue(): void {
  if (!posthog.__loaded) return;

  // Splice first: a captureException that somehow re-enters must not see the
  // entry it is replaying and loop.
  const queued = preInitQueue.splice(0, preInitQueue.length);
  for (const item of queued) {
    captureToPostHog(
      item.error,
      item.feature,
      item.operation,
      item.context,
      item.level
    );
  }
}

/**
 * Best-effort human-readable message for a raw, possibly non-Error value —
 * used ONLY to build a grouping fingerprint (see {@link reportUnhandledToPostHog}),
 * never sent as the captured exception itself.
 */
function unhandledErrorMessage(value: unknown): string {
  if (typeof value === 'string') return value;
  if (
    value &&
    typeof value === 'object' &&
    'message' in value &&
    typeof (value as { message?: unknown }).message === 'string'
  ) {
    return (value as { message: string }).message;
  }
  return String(value);
}

/** Classify the original before scrubbing erases the known noise phrases. */
function isNoiseError(error: unknown): boolean {
  const source =
    error && typeof error === 'object'
      ? (error as { message?: unknown; stack?: unknown })
      : undefined;
  const stack = typeof source?.stack === 'string' ? source.stack : '';
  const filenames = stack.match(/[a-z][a-z-]*:\/\/[^\s)]+/gi) ?? [];
  return !!classifyDroppableEvent({
    message: unhandledErrorMessage(error),
    exception: {
      values: [
        { stacktrace: { frames: filenames.map((filename) => ({ filename })) } },
      ],
    },
  });
}

/** Group arbitrary thrown strings without exporting the source text. */
function diagnosticFingerprint(message: string): string {
  let hash = 2166136261;
  for (let index = 0; index < message.length; index++) {
    hash = Math.imul(hash ^ message.charCodeAt(index), 16777619);
  }
  return (hash >>> 0).toString(16);
}

/** An error-like value from another browser realm still has useful frames. */
function isErrorLike(
  value: unknown
): value is { name?: unknown; message: string; stack?: unknown } {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as { message?: unknown }).message === 'string'
  );
}

/** Rebuild the error before the telemetry boundary copies and scrubs it. */
function toSameRealmError(value: {
  name?: unknown;
  message: string;
  stack?: unknown;
}): Error {
  const err = new Error(value.message);
  if (typeof value.name === 'string' && value.name) err.name = value.name;
  if (typeof value.stack === 'string' && value.stack) err.stack = value.stack;
  return err;
}

export function reportUnhandledToPostHog(
  error: unknown,
  source: 'window.onerror' | 'unhandledrejection'
): void {
  const extra: Record<string, unknown> = {};

  let capturable: unknown = error;
  if (!(error instanceof Error) && isErrorLike(error)) {
    // Same-realm Error with the original stack: the SDK groups on its real
    // frames and the noise filter can see where it came from.
    capturable = toSameRealmError(error);
  } else if (!(error instanceof Error)) {
    // Strings / non-error objects can only get the SDK's synthetic stack, so
    // group them by message ourselves or they all collapse into one issue.
    const message = unhandledErrorMessage(error);
    extra.$exception_fingerprint = `unhandled:${source}:${diagnosticFingerprint(message)}`;
  }

  captureToPostHog(capturable, 'unhandled', source, { extra });
  forwardToBetterStack(source, capturable);
}

/** Keep the source Error shape while adding the expected-condition tag. */
function toLoggableError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (isErrorLike(error)) return toSameRealmError(error);
  if (error && typeof error === 'object') {
    try {
      const json = JSON.stringify(error);
      // `JSON.stringify` returns `undefined` for values it can't serialize
      // (functions, symbols) and `"{}"` for an object with no own
      // enumerable properties — neither is more useful than `String()`.
      if (json && json !== '{}') return new Error(json);
    } catch {
      // Circular reference or a thrown class instance with getters that
      // throw — fall through to the generic String() below.
    }
  }
  return new Error(String(error));
}

interface ApiFetchErrorShape {
  name: 'ApiFetchError';
  status: number;
  method: string;
  path: string;
  requestId?: string;
  code?: string;
}

function isApiFetchErrorLike(
  error: unknown
): error is Error & ApiFetchErrorShape {
  return (
    !!error &&
    typeof error === 'object' &&
    (error as { name?: unknown }).name === 'ApiFetchError'
  );
}

/**
 * HTTP errors all point at the same transport frame, so PostHog otherwise
 * groups unrelated endpoints and response classes into one issue.
 * Operation + status + stable API code separates failures without putting
 * dynamic URLs, record IDs or provider messages into the fingerprint.
 */
function httpErrorFingerprint(
  error: unknown,
  operation: string
): string | undefined {
  if (!error || typeof error !== 'object') return undefined;

  if (isApiFetchErrorLike(error)) {
    const code = error.code || 'unknown';
    return `http:${operation}:${error.status}:${code}`;
  }

  const candidate = error as {
    name?: unknown;
    code?: unknown;
    response?: { status?: unknown };
  };
  if (candidate.name !== 'HTTPError') return undefined;

  const status =
    typeof candidate.response?.status === 'number'
      ? candidate.response.status
      : 'unknown';
  const code =
    typeof candidate.code === 'string' && candidate.code.length > 0
      ? candidate.code
      : 'unknown';
  return `http:${operation}:${status}:${code}`;
}

export function logError(
  operation: string,
  error: unknown,
  context?: LogErrorContext
): void {
  const err = sanitizeTelemetryError(error);
  const safeContext = sanitizeTelemetry(context);
  const feature = safeContext?.feature ?? operation.split('.')[0];
  const fingerprint = httpErrorFingerprint(error, operation);
  const posthogContext = fingerprint
    ? {
        ...safeContext,
        extra: {
          $exception_fingerprint: fingerprint,
          ...safeContext?.extra,
        },
      }
    : safeContext;

  console.error(
    `[${sanitizeTelemetry({ operation }).operation}]`,
    err.message,
    safeContext?.extra ?? ''
  );

  captureToPostHog(error, feature, operation, posthogContext);
  forwardToBetterStack(operation, err);

  if (isNoiseError(error)) return;
  const safeLabels = sanitizeTelemetry({ feature, operation });
  Sentry.withScope((scope) => {
    scope.setTag('feature', safeLabels.feature);
    scope.setTag('operation', safeLabels.operation);
    scope.setTag('posthog_loaded', String(posthog.__loaded));
    if (isApiFetchErrorLike(error)) {
      scope.setTag('api.status', String(error.status));
      scope.setTag(
        'api.method',
        sanitizeTelemetry({ method: error.method }).method
      );
      scope.setTag('api.path', sanitizeTelemetryUrl(error.path));
      if (error.requestId) {
        scope.setTag(
          'api.requestId',
          sanitizeTelemetry({ requestId: error.requestId }).requestId
        );
      }
    }
    if (safeContext?.tags) {
      for (const [key, value] of Object.entries(safeContext.tags)) {
        scope.setTag(key, value);
      }
    }
    if (safeContext?.extra) {
      scope.setExtras(safeContext.extra);
    }
    Sentry.captureException(err);
  });
}

/**
 * Report a UI-handled failure at error level, prefixed "Expected behaviour: "
 * and tagged `expected: true` so it can be filtered without being silenced.
 */
export function logExpectedError(
  operation: string,
  error: unknown,
  context?: LogErrorContext
): void {
  const err = toLoggableError(error);
  const reported = Object.assign(
    new Error(`Expected behaviour: ${err.message}`),
    err
  );
  reported.name = err.name;
  if (err.stack) reported.stack = err.stack;
  logError(operation, reported, {
    ...context,
    tags: { expected: 'true', ...context?.tags },
  });
}

/**
 * Log a warning to the console, PostHog and Sentry.
 * Use for non-critical issues that should be tracked (e.g., degraded state).
 *
 * Dual-sends to PostHog exactly like {@link logError}. Until 2026-08 this went
 * to Sentry ONLY, so warning-level issues (e.g. `upload.analysisTimeout`) were
 * invisible in PostHog — a hole that would have survived a cutover to PostHog
 * as the primary error source. PostHog Error Tracking has no separate "message"
 * concept, so a warning is sent as an `$exception` with
 * `$exception_level: 'warning'` (mirroring what `Sentry.captureMessage` does
 * internally: synthesize an error to carry the stack). Filter it out with
 * `properties.$exception_level != 'warning'` when you want errors only.
 */
export function logWarning(
  operation: string,
  message: string,
  context?: LogErrorContext
): void {
  const safeContext = sanitizeTelemetry(context);
  const safeMessage = sanitizeTelemetryText(message);
  const feature = safeContext?.feature ?? operation.split('.')[0];

  console.warn(
    `[${sanitizeTelemetry({ operation }).operation}]`,
    safeMessage,
    safeContext?.extra ?? ''
  );

  // The synthesized Error's stack points at this call site, which is the useful
  // frame for a warning that has no thrown error of its own.
  const warning = new Error(message);
  captureToPostHog(warning, feature, operation, safeContext, 'warning');
  forwardToBetterStack(operation, warning, 'warn');

  if (isNoiseError(warning)) return;
  Sentry.captureMessage(
    `[${sanitizeTelemetry({ operation }).operation}] ${safeMessage}`,
    sanitizeTelemetry({
      level: 'warning',
      tags: {
        feature,
        operation,
        // Same diagnostic as logError: if posthog_loaded=false shows up here,
        // the PostHog copy of this warning was dropped before init.
        posthog_loaded: String(posthog.__loaded),
        ...safeContext?.tags,
      },
      extra: safeContext?.extra,
    })
  );
}
